import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useSavedSearches, NotificationCadence } from '../../hooks/useSavedSearches';
import { Bookmark, Share2, Bell, BellOff } from 'lucide-react';

const notificationCategories = [
  {
    title: 'Job matches',
    description: 'Weekly digest of roles that align with your preferences.',
    key: 'matches',
  },
  {
    title: 'Application updates',
    description: 'Instant alerts when employers review or message you.',
    key: 'applications',
  },
  {
    title: 'Returner programmes',
    description: 'Invites to mentorship, events, and training sessions.',
    key: 'programmes',
  },
  {
    title: 'Product updates',
    description: 'Release notes and new feature walkthroughs.',
    key: 'product',
  },
];

const cadenceOptions: { value: NotificationCadence; label: string }[] = [
  { value: 'daily', label: 'Daily digest' },
  { value: 'weekly', label: 'Weekly summary' },
  { value: 'off', label: 'No email notifications' },
];

export function NotificationCenter() {
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [preferences, setPreferences] = useState(() => notificationCategories.map((item) => item.key));
  const {
    savedSearches,
    updateCadence,
    defaultCadence,
    setDefaultCadence,
    shareSavedSearch,
    pushEnabled,
    setPushEnabled,
    savedSearchesLoading,
    savedSearchesError,
    settingsLoading,
    settingsError,
  } = useSavedSearches();

  const formatCurrency = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return null;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const describeSavedSearch = (search: typeof savedSearches[number]) =>
    `${search.query || 'Any role'} · ${search.location || 'Any location'}`;

  const getPaySummary = (search: typeof savedSearches[number]) => {
    const minLabel = formatCurrency(search.minPay);
    const maxLabel = formatCurrency(search.maxPay);
    if (minLabel && maxLabel) return `Pay: ${minLabel} – ${maxLabel}`;
    if (minLabel) return `Pay: ${minLabel}+`;
    if (maxLabel) return `Pay: up to ${maxLabel}`;
    return '';
  };

  const togglePreference = (key: string) => {
    setPreferences((prev) => (prev.includes(key) ? prev.filter((value) => value !== key) : [...prev, key]));
  };

  return (
    <main aria-labelledby="notification-center-heading" className="min-h-screen bg-[#070917] pb-24">
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="space-y-2">
          <h1 id="notification-center-heading" className="text-3xl font-semibold text-white sm:text-4xl">Notification preferences</h1>
          <p className="text-sm text-gray-400">
            Decide how Lighthouse keeps you updated across email, SMS, push, and in-app alerts.
          </p>
        </header>

        <div className="mt-10 space-y-6">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Channels</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0">
              <div role="group" aria-label="Channel preferences" className="space-y-4">
              <label className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div>
                  <p className="font-medium text-white">Email</p>
                  <p className="text-xs text-gray-400">Best for updates that can wait a few hours.</p>
                </div>
                <Switch checked={emailEnabled} onCheckedChange={(checked) => setEmailEnabled(Boolean(checked))} />
              </label>
              <label className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div>
                  <p className="font-medium text-white">SMS</p>
                  <p className="text-xs text-gray-400">Use for time-sensitive actions like interview confirmations.</p>
                </div>
                <Switch checked={smsEnabled} onCheckedChange={(checked) => setSmsEnabled(Boolean(checked))} />
              </label>
              <label className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div>
                  <p className="font-medium text-white">Push</p>
                  <p className="text-xs text-gray-400">
                    Enable real-time saved search alerts. We’ll request browser permission.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => setPushEnabled(!pushEnabled)}
                >
                  {pushEnabled ? <Bell className="mr-2 h-4 w-4" /> : <BellOff className="mr-2 h-4 w-4" />}
                  {pushEnabled ? 'Disable' : 'Enable'}
                </Button>
              </label>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Topics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0">
              {notificationCategories.map((category) => (
                <label key={category.key} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4">
                  <Checkbox
                    id={category.key}
                    checked={preferences.includes(category.key)}
                    onCheckedChange={() => togglePreference(category.key)}
                  />
                  <div>
                    <p className="font-medium text-white">{category.title}</p>
                    <p className="text-xs text-gray-400">{category.description}</p>
                  </div>
                </label>
              ))}
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Saved search alerts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 p-6 pt-0 text-sm text-gray-300">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Default cadence</p>
                <Select
                  value={defaultCadence}
                  disabled={settingsLoading}
                  onValueChange={(value) => setDefaultCadence(value as NotificationCadence)}
                >
                  <SelectTrigger className="w-full border-white/10 bg-white/10 text-white disabled:opacity-60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                    {cadenceOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {settingsLoading && <p className="text-xs text-gray-500">Loading your default cadence...</p>}
                {settingsError && !settingsLoading && (
                  <p className="text-xs text-red-300">Unable to fetch notification settings.</p>
                )}
              </div>

              {savedSearchesLoading && <p className="text-gray-400">Loading saved searches...</p>}
              {savedSearchesError && !savedSearchesLoading && (
                <p className="text-sm text-red-300">We could not load your saved searches. Refresh and try again.</p>
              )}
              {!savedSearchesLoading && !savedSearchesError && (
                <>
                  {savedSearches.length === 0 ? (
                    <p className="text-gray-400">Save a search on the jobs page to see alerts here.</p>
                  ) : (
                    <div className="space-y-3">
                      {savedSearches.map((search) => (
                        <div key={search.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.04] p-3">
                          <div className="space-y-1">
                            <p className="flex items-center gap-2 text-white">
                              <Bookmark className="h-4 w-4 text-[#f7ef8a]" aria-hidden />
                              {search.name}
                            </p>
                            <p className="text-xs text-gray-400">{describeSavedSearch(search)}</p>
                            {search.tags.length > 0 && (
                              <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">
                                Tags: {search.tags.join(', ')}
                              </p>
                            )}
                            {getPaySummary(search) && (
                              <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">{getPaySummary(search)}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Select
                              value={search.cadence}
                              onValueChange={(value) => updateCadence(search.id, value as NotificationCadence)}
                            >
                              <SelectTrigger className="h-9 min-w-[10rem] border-white/15 bg-white/10 text-xs text-gray-100">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                                {cadenceOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-gray-300 hover:text-[#f7ef8a]"
                              onClick={() => shareSavedSearch(search)}
                            >
                              <Share2 className="mr-2 h-4 w-4" />
                              Share
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
              Cancel
            </Button>
            <Button className="bg-gold-gradient text-black hover:brightness-110">
              Save preferences
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}


